import React from 'react';
import { PaymentPlan, PaymentPlanItem, PaymentStatus, PaymentConcept, PAYMENT_CONCEPT_LABELS, PAYMENT_STATUS_LABELS, PAYMENT_STATUS_COLORS } from '../../types/payment-plan';
import { ButtonV2 } from '../ButtonV2/ButtonV2';
import { Check, Clock, AlertCircle, Calendar } from 'lucide-react';
import Pill from '../Pill/Pill';
import { useTranslation } from 'react-i18next';

interface CommissionPaymentPlanDisplayProps {
  paymentPlan: PaymentPlan;
  onPayNow?: (item: PaymentPlanItem) => void;
  showPayButtons?: boolean;
  isEditable?: boolean;
  onEdit?: () => void;
  onlinePaymentsEnabled?: boolean;
}

const CommissionPaymentPlanDisplay: React.FC<CommissionPaymentPlanDisplayProps> = ({
  paymentPlan,
  onPayNow,
  showPayButtons = true,
  isEditable = false,
  onEdit,
  onlinePaymentsEnabled = false
}) => {
  const { t } = useTranslation();

  const getStatusColor = (status: PaymentStatus) => {
    return PAYMENT_STATUS_COLORS[status] as any;
  };

  const isOverdue = (dueDate?: string) => {
    if (!dueDate) return false;
    const dueDateTime = dueDate.length <= 10 ? `${dueDate}T23:59:59` : dueDate;
    const due = new Date(dueDateTime);
    const now = new Date();
    return due.getTime() < now.getTime();
  };

  const getPaymentIcon = (item: PaymentPlanItem) => {
    if (item.status === PaymentStatus.PAID) {
      return <Check className="h-4 w-4 text-green-600" />;
    }
    if (isOverdue(item.dueDate)) {
      return <AlertCircle className="h-4 w-4 text-red-600" />;
    }
    if (item.dueDate) {
      return <Calendar className="h-4 w-4 text-gray-600" />;
    }
    return <Clock className="h-4 w-4 text-gray-600" />;
  };

  const getPaymentStatusText = (item: PaymentPlanItem) => {
    if (item.status === PaymentStatus.PAID) {
      return t('commission_payment_plan.paid') || 'Paid';
    }
    if (isOverdue(item.dueDate)) {
      return t('commission_payment_plan.overdue') || 'Overdue';
    }
    return t(PAYMENT_STATUS_LABELS[item.status]) || item.status;
  };

  const getPaymentStatusColor = (item: PaymentPlanItem) => {
    if (isOverdue(item.dueDate) && item.status !== PaymentStatus.PAID) {
      return 'red';
    }
    return getStatusColor(item.status);
  };

  const canPay = (item: PaymentPlanItem) => {
    return item.status === PaymentStatus.NOT_PAID || 
           item.status === PaymentStatus.PENDING ||
           item.status === PaymentStatus.OVERDUE;
  };


  return (
    <div className="space-y-4">
      {/* Header */}
      {isEditable && onEdit && (
        <div className="flex justify-end">
          <ButtonV2
            variant="outline"
            size="sm"
            onClick={onEdit}
          >
            {t('commission_payment_plan.edit') || 'Edit'}
          </ButtonV2>
        </div>
      )}


      {/* Payment Items */}
      <div className="space-y-3">
        {paymentPlan.items.filter(i => !i.isDisabled).map((item, index) => (
          <div key={item.id} className="border rounded-lg p-4 bg-white">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  {getPaymentIcon(item)}
                  <h4 className="font-medium text-gray-900">
                    {t('commission_payment_plan.payment') || 'Payment'} {index + 1}
                  </h4>
                </div>
                
                <div className="text-sm text-gray-600 mb-2">
                  {t(PAYMENT_CONCEPT_LABELS[item.concept as PaymentConcept]) || item.concept}
                </div>
                
                {item.description && (
                  <p className="text-sm text-gray-500 mb-3">
                    {item.description}
                  </p>
                )}

                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-1">
                    <span className="font-medium">
                      {item.amount.toFixed(2)} {item.currency}
                    </span>
                  </div>
                  
                  {item.dueDate && (
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4 text-gray-400" />
                      <span className={isOverdue(item.dueDate) ? 'text-red-600' : 'text-gray-600'}>
                        {new Date(item.dueDate).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                  {(
                    <div className="flex items-center gap-1 text-gray-600">
                      <span>
                        {(t('store_commission_panel.commission_payment_plan.valid_for') || 'Valid for') + ' ' + item.validForDays + ' ' + (t('store_commission_panel.commission_payment_plan.days') || 'days')}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex flex-col items-end gap-2">
                <Pill
                  text={getPaymentStatusText(item)}
                  color={getPaymentStatusColor(item)}
                />
                
                {showPayButtons && onlinePaymentsEnabled && canPay(item) && onPayNow && (
                  <ButtonV2
                    size="sm"
                    onClick={() => onPayNow(item)}
                    disabled={!canPay(item)}
                  >
                    {t('commission_payment_plan.pay_now') || 'Pay Now'}
                  </ButtonV2>
                )}
              </div>
            </div>

            {/* Payment Details (if paid) */}
            {item.status === PaymentStatus.PAID && item.paidDate && (
              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2 text-green-800">
                  <Check className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    {t('commission_payment_plan.paid_on') || 'Paid on'}: {new Date(item.paidDate).toLocaleDateString()}
                  </span>
                </div>
                {item.paymentMethod && (
                  <p className="text-xs text-green-600 mt-1">
                    {t('commission_payment_plan.payment_method') || 'Method'}: {item.paymentMethod}
                  </p>
                )}
                {item.invoiceReference && (
                  <p className="text-xs text-green-600 mt-1">
                    {t('commission_payment_plan.invoice_reference') || 'Reference'}: {item.invoiceReference}
                  </p>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* No payments message */}
      {paymentPlan.items.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <p>{t('commission_payment_plan.no_payments') || 'No payments configured'}</p>
        </div>
      )}
    </div>
  );
};

export default CommissionPaymentPlanDisplay;
